<template>
  <AccountWrapper>
    <div v-if="codeVisible" class="code-wrapper">
      <div class="code">
        <div class="code__inner">
          <AccountIcon4 class="code__icon" />
          <div class="code__title">
            {{ t('account.referral.instructions.title') }}
          </div>
          <div class="code__text">
            {{ t('account.referral.instructions.subtitle') }}
          </div>

          <div class="code__content">
            <span>{{ user?.data?.referralCoupon?.data?.code }}</span>
            <Button
              color="primary"
              variant="outlined"
              class="copy"
              @click="copyCode"
            >
              <span class="flex items-center gap-2">
                <IconCopy class="copy__icon" />
                {{ t('account.referral.codeInfo.shareButton') }}
              </span>
            </Button>
          </div>
        </div>
      </div>

      <ol>
        <li>{{ t('account.referral.instructions.list[0]') }}</li>
        <li>{{ t('account.referral.instructions.list[1]') }}</li>
        <li>{{ t('account.referral.instructions.list[2]') }}</li>
      </ol>
    </div>

    <FAQ :content="faqItems" class="faq !p-0" />
  </AccountWrapper>
</template>

<script setup lang="ts">
import { toast, ToastOptions } from 'vue3-toastify'
import FAQ from '~/components/sections/FAQ.vue'
import AccountIcon4 from '~/assets/account/account-icon-4.svg?component'

const { user } = useAccount()
const { t } = useI18n()

const codeVisible = computed(() => {
  return (
    user?.value?.data?.referralCoupon?.data?.status &&
    user?.value?.data?.referralCoupon?.data?.status === 'ACTIVE'
  )
})

const faqItems = computed(() => {
  const length = 4
  const questions = Array.from(
    {
      length,
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    function (v, k) {
      return {
        question: t(`account.referral.faq.items[${k}].question`),
        answer: t(`account.referral.faq.items[${k}].answer`),
      }
    }
  )

  return { questions }
})

const copyCode = () => {
  navigator.clipboard.writeText(user?.value?.data?.referralCoupon?.data?.code)

  toast(t('general.success'), {
    autoClose: 2000,
    type: 'success',
    position: toast.POSITION.TOP_CENTER,
    multiple: false,
  } as ToastOptions)
}

definePageMeta({
  middleware: ['auth'],
  requiresAuth: true,
  isSimpleFooter: true,
})
</script>

<style scoped lang="postcss">
ol {
  list-style: none;
  counter-reset: item;
  @apply p-0 max-w-[500px] w-full mx-auto flex flex-col gap-3;

  li {
    counter-increment: item;

    &::before {
      content: counter(item);
      margin-right: 8px;
      @apply bg-lime-500 text-primary text-sm font-medium px-2 py-1 rounded-full w-[18px] h-[18px] inline-flex items-center justify-center;
    }
  }
}

.code-wrapper {
  box-shadow: 0 4px 35px 0 rgba(181, 181, 195, 0.15);
  @apply bg-white p-2.5 pb-6 rounded-xl;
}

.code {
  @apply relative mb-6 p-4 rounded-lg bg-lime-50;

  &__inner {
    @apply flex flex-col items-center max-w-[500px] w-full mx-auto;
  }

  &__title {
    @apply text-lg text-teal-900 font-semibold;
  }

  &__icon {
    @apply w-[30px] h-[30px] mb-4 mx-auto;
  }

  &__content {
    @apply relative w-full bg-white flex items-center gap-2 justify-center md:justify-between border border-gray-200 rounded-lg pl-4 pr-1 pt-3 pb-14 md:pt-1 md:pb-1;
  }

  &__text {
    @apply text-left text-sm text-gray-500 mb-3 font-medium;
  }

  .copy {
    @apply absolute bottom-1 left-1 right-1 md:relative md:bottom-auto md:right-auto md:left-auto px-3 text-xs font-medium;
  }
}

.faq {
  @apply mb-0;

  &-item {
    @apply mb-6;

    &__question {
      @apply font-medium text-lg;
    }

    &:last-child {
      @apply mb-0;
    }
  }
}
</style>
